import { useState } from 'react';

import { useIntl } from '../../hooks';
import { type Device } from '../../types';
import { DeviceFamily } from '../../validations/device-form.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui';
import DeviceScanRangesPanel from './DeviceScanRangesPanel';
import DeviceSettingsPanel from './DeviceSettingsPanel';
import DeviceWhitelistPanel from './DeviceWhitelistPanel';

interface DeviceSettingsDialogProps {
  device: Device;
  onOpenChange: () => void;
}

export default function DeviceSettingsDialog({ device, onOpenChange }: DeviceSettingsDialogProps) {
  const intl = useIntl();
  const tabs = [
    {
      name: 'Settings',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.settings' }),
      Component: DeviceSettingsPanel,
    },
    {
      name: 'Whitelist',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.whitelist' }),
      Component: DeviceWhitelistPanel,
    },
    {
      name: 'Scan ranges',
      label: intl.formatMessage({ id: 'deviceSettings.tabs.scanRanges' }),
      Component: DeviceScanRangesPanel,
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0].name);
  const settingsOnly = device.model !== DeviceFamily.KVITKA;
  // Without a TabsList the panel has no trigger to be labelled by, so it is not a tabpanel.
  const settingsOnlyContentProps = settingsOnly
    ? { role: undefined, 'aria-labelledby': undefined, tabIndex: undefined }
    : {};

  return (
    <Dialog open={true} onOpenChange={onOpenChange}>
      {/* overflow-clip (not -hidden): an overflow:hidden box is still programmatically
          scrollable, so focusing the sr-only directionality radio makes the browser
          scroll this container to "reveal" it, detaching the footer. clip can't scroll. */}
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col overflow-clip">
        <DialogTitle hidden></DialogTitle>
        <div className="flex-1 overflow-hidden">
          {/* One Tabs tree for every family, so a family pick does not remount the settings panel. */}
          <Tabs
            value={settingsOnly ? tabs[0].name : activeTab}
            onValueChange={setActiveTab}
            className="h-full flex flex-col"
          >
            {!settingsOnly && (
              <TabsList className="grid w-full grid-cols-3 shrink-0 bg-transparent border-b border-border rounded-none p-0">
                {tabs.map(({ name, label }) => (
                  <TabsTrigger
                    key={`${name}-tab-trigger`}
                    value={name}
                    className="data-[state=active]:bg-accent-light data-[state=active]:text-accent data-[state=active]:border-b-2 data-[state=active]:border-accent-dark rounded-none border-b-2 border-transparent"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>
            )}

            <div className="flex-1 overflow-hidden">
              {(settingsOnly ? tabs.slice(0, 1) : tabs).map(({ name, Component }) => (
                <TabsContent
                  key={`${name}-tab-content`}
                  value={name}
                  className="h-full overflow-hidden"
                  {...settingsOnlyContentProps}
                >
                  <Component device={device} />
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
